/**
 * Life4Earth V2 — Suspension Overlay (DEV-147)
 *
 * Non-dismissable overlay shown when the server returns ACCOUNT_SUSPENDED.
 * Listens for the custom 'l4e:account-suspended' event dispatched by apiFetch.
 * The overlay blocks all interaction until the session expires naturally.
 */

import React, { useState, useEffect } from 'react';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.85)',
  backdropFilter: 'blur(8px)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const cardStyle: React.CSSProperties = {
  maxWidth: 420,
  padding: '40px 36px',
  background: 'var(--bg-elevated, #1e1e2e)',
  borderRadius: 16,
  border: '1px solid var(--border-default, #333)',
  textAlign: 'center',
};

const iconStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  background: 'rgba(239, 68, 68, 0.12)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px',
  fontSize: 28,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--text-primary, #f0f0f0)',
  margin: '0 0 12px 0',
};

const messageStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-secondary, #999)',
  lineHeight: 1.6,
  margin: 0,
};

export default function SuspensionOverlay() {
  const [suspended, setSuspended] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setMessage(detail?.message || 'Your account has been suspended.');
      setSuspended(true);
    };

    window.addEventListener('l4e:account-suspended', handler);
    return () => window.removeEventListener('l4e:account-suspended', handler);
  }, []);

  if (!suspended) return null;

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={iconStyle}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h2 style={titleStyle}>Account Suspended</h2>
        <p style={messageStyle}>
          {message}
        </p>
        <p style={{ ...messageStyle, marginTop: 16, fontSize: 12, color: 'var(--text-muted, #666)' }}>
          If you believe this is an error, please contact your community administrator.
        </p>
      </div>
    </div>
  );
}
